import CustomSelect from 'components/custom-select';
import { FOCO_VISIVEL } from 'utils/foco';

import { OPCOES_DE_ORDEM, OrdemDaFila } from '../ordenar-fila';

/** Opção dos filtros atuais da fila (`feedbackFilterOptions`, `taskStatusOptions`), com o rótulo já contado. */
export interface OpcaoDeFiltro {
  label: string;
  value: string;
}

export interface FiltrosDaFilaProps {
  busca: string;
  onBusca: (texto: string) => void;
  /** Mesma condição da página atual para mostrar os seletores de feedback e status. */
  mostrarSeletores: boolean;
  opcoesDeFeedback: OpcaoDeFiltro[];
  feedback: string | undefined;
  onFeedback: (valor: string) => void;
  opcoesDeStatus: OpcaoDeFiltro[];
  status: string | undefined;
  onStatus: (valor: string) => void;
  ordem: OrdemDaFila;
  onOrdem: (ordem: OrdemDaFila) => void;
  /** "Limpar filtros" aparece quando há busca ou filtro aplicado. */
  filtrosAtivos: boolean;
  onLimparFiltros: () => void;
}

const ID_DA_BUSCA = 'fila-busca';

/** "Limpar filtros" some depois do clique: o foco vai para a busca, o primeiro filtro (WCAG 2.4.3). */
export function limparEFocarNaBusca(onLimparFiltros: () => void) {
  onLimparFiltros();
  document.getElementById(ID_DA_BUSCA)?.focus();
}

const ROTULO = 'mb-[7px] block text-[10px] font-bold uppercase tracking-[0.06em] text-content-soft';
const CAMPO = `w-full rounded-jj-field border border-content-soft bg-brand-white px-3.5 py-[11px] text-[13px] text-content ${FOCO_VISIVEL}`;

function Seletor({
  id,
  rotulo,
  opcoes,
  valor,
  onEscolher,
}: {
  id: string;
  rotulo: string;
  opcoes: OpcaoDeFiltro[];
  valor: string | undefined;
  onEscolher: (valor: string) => void;
}) {
  return (
    <div className="min-w-[150px] flex-[1_1_100%] sm:flex-[0_1_180px]">
      <label htmlFor={id} className={ROTULO}>
        {rotulo}
      </label>
      <CustomSelect
        inputId={id}
        ariaLabel={rotulo}
        variant="light"
        options={opcoes}
        value={opcoes.find(opcao => opcao.value === valor) ?? opcoes[0]}
        onChange={(opcao: any) => opcao && onEscolher(opcao.value)}
      />
    </div>
  );
}

/**
 * Busca, filtros de feedback e status e "Ordenar por" da fila (RDN-46, RDN-49). Recebe as opções e o estado que a
 * página já mantém; escolher uma opção chama o mesmo handler que o seletor antigo chamava.
 */
export default function FiltrosDaFila(props: FiltrosDaFilaProps) {
  return (
    <div className="flex flex-wrap items-end gap-2.5">
      <div className="min-w-[200px] flex-[1_1_220px]">
        <label htmlFor="fila-busca" className={ROTULO}>
          Buscar na lista
        </label>
        <input
          id={ID_DA_BUSCA}
          type="search"
          value={props.busca}
          onChange={evento => props.onBusca(evento.target.value)}
          placeholder="Nome da realização, funcionário ou chave"
          className={CAMPO}
        />
      </div>

      {props.mostrarSeletores && (
        <>
          <Seletor
            id="fila-feedback"
            rotulo="Feedback"
            opcoes={props.opcoesDeFeedback}
            valor={props.feedback}
            onEscolher={props.onFeedback}
          />
          <Seletor id="fila-status" rotulo="Status" opcoes={props.opcoesDeStatus} valor={props.status} onEscolher={props.onStatus} />
        </>
      )}

      <Seletor
        id="fila-ordem"
        rotulo="Ordenar por"
        opcoes={OPCOES_DE_ORDEM.map(({ valor, rotulo }) => ({ value: valor, label: rotulo }))}
        valor={props.ordem}
        onEscolher={valor => props.onOrdem(valor as OrdemDaFila)}
      />

      {props.filtrosAtivos && (
        <button
          type="button"
          onClick={() => limparEFocarNaBusca(props.onLimparFiltros)}
          className={`mb-3 ml-auto cursor-pointer border-0 bg-transparent p-0 text-[11px] font-bold uppercase tracking-[0.05em] text-brand-blue ${FOCO_VISIVEL}`}
        >
          Limpar filtros
        </button>
      )}
    </div>
  );
}
